import {
  Box,
  Button,
  CardMedia,
  Dialog,
  Fade,
  Grid,
  IconButton,
  Slide,
  Typography,
  useTheme,
} from "@mui/material";
import { useLenis } from "../../hooks/useLenis";
import { CanvasAnimated } from "../../components/CanvasAnimated";
import { ScrollAnimatedPin } from "../../components/ScrollAnimatedPin";
import { Title } from "../../components/Title";
import { CardCategory } from "../../components/CardCategory";
import { faturedData } from "../../helpers/Featured";
import { finalChanceData } from "../../helpers/FinalChance";
import { EmblaHome } from "../../components/EmblaHome/EmblaHome";
import { ArrowSplideRight, Close } from "../../icons";
import logo from "../../assets/logo.png";
import React, { useEffect, useState } from "react";
import { TransitionProps } from "@mui/material/transitions";
import { HeroVideo } from "../../components/HeroVideo";

const SESSION_KEY = "home_modal";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const Home = () => {
  useLenis(1.2);

  const [showModal, setShowModal] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const closed = sessionStorage.getItem(SESSION_KEY);
    if (!closed) {
      setTimeout(() => {
        setShowModal(true);
      }, 1000);
    }
  }, []);

  const onModalClose = () => {
    sessionStorage.setItem(SESSION_KEY, "true");
    setShowModal(false);
  };

  const onNavigateNikolayFitting = () => {
    window.open(
      "https://calendly.com/nikolayworld/nikolay-orlando-showroom?back=1&month=2025-10",
      "_blank",
    );
  };


  return (
    <Fade in timeout={2000}>
      <Box position="relative">
        <HeroVideo />
        <Box display={{ xs: "none", md: "flex" }}>
          <Title
            bgColor="black"
            boxShadow
            title="Where Ballet"
            subTitle="Dreams Begin"
          />
        </Box>
        <ScrollAnimatedPin>
          <CanvasAnimated
            bgColor="rgba(0, 0, 0, 0.8)"
            title="Inspire. Move. Shine."
            firstSubtitle="Passion for ballet, brought to life."
            secondSubtitle="Elegance in every"
            featured="Nikolay"
            lastText="piece."
            frameCount={56}
            file="little_girl"
          />
        </ScrollAnimatedPin>
        <Title topShadow subTitle="Featured Products" />
        <Grid
          position="relative"
          display="flex"
          justifyContent="center"
          mb={10}
          marginInline={3}
          container
          spacing={2}
        >
          <Box width="100%" position="absolute" top={0} />

          {faturedData.map((f) => (
            <CardCategory
              key={f.id}
              size={{ xs: 6, sm: 4, lg: 3 }}
              src={f.image}
              label={f.name}
              route={f.route}
            />
          ))}
        </Grid>
        <Box
          mt={30}
          height={{ xs: 550, md: "100svh" }}
          sx={{
            width: "100%",
            gap: { xs: 0, md: 15 },
            position: "relative",
            zIndex: 999,
            backgroundColor: "black",
            boxShadow: "0px 0px 50px 80px black",
          }}
        >
          <Title fontSizeTitle={45} title="Final Chance" />
          <EmblaHome
            slides={finalChanceData}
            isLoading={false}
            options={{ dragFree: true }}
          />
        </Box>

        <ScrollAnimatedPin>
          <CanvasAnimated
            showLogo
            bgColor="white"
            frameCount={45}
            file="good_bye"
          />
        </ScrollAnimatedPin>
        <Dialog
          keepMounted
          disableScrollLock
          open={showModal}
          onClose={onModalClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          slotProps={{
            transition: { timeout: 500 },
            paper: {
              sx: {
                width: { xs: "100%", sm: 550, md: 660 },
                height: { xs: "80svh", md: "90svh" },
                borderRadius: 4,
                marginInline: 2,
                backgroundColor: theme.palette.brand?.light,
              },
            },
          }}
          slots={{
            transition: Transition,
          }}
        >
          <CardMedia
            width="100%"
            height="100%"
            component="img"
            sx={{ objectFit: "contain" }}
            image="https://yzlbdstwuaznspepvtyo.supabase.co/storage/v1/object/public/nikolay/announcement/appointment.png"
            alt=""
          />

          <Box
            display="flex"
            justifyContent="space-between"
            position="absolute"
            width="100%"
          >
            <Box padding={{ xs: 5, md: 7 }}>
              <Typography
                className="title-schelude"
                sx={{
                  fontSize: { xs: 25, md: 35 },
                  textTransform: "uppercase",
                  fontWeight: 900,
                  color: theme.palette.secondary[700],
                }}
              >
                Schedule Your
              </Typography>
              <Typography
                className="title-appointment"
                sx={{
                  fontSize: { xs: 17, md: 24 },
                  textTransform: "uppercase",
                  fontWeight: 900,
                  color: theme.palette.secondary[700],
                }}
              >
                Appointment
              </Typography>
            </Box>

            <IconButton
              onClick={onModalClose}
              sx={{
                borderRadius: "100vmax !important",
                position: "absolute",
                top: 4,
                right: 4,
              }}
            >
              <Close
                sx={{ width: { xs: 25, md: 30 }, height: { xs: 25, md: 30 } }}
              />
            </IconButton>
          </Box>

          <Box
            width="100%"
            display="flex"
            alignItems={{ xs: "center", md: "flex-end" }}
            justifyContent="space-between"
            position="absolute"
            bottom={20}
          >
            <img style={{ marginInline: 20 }} src={logo} height={30} alt="" />

            <Box marginInline={3} display="flex" flexDirection="column" gap={3}>
              <Button
                size="small"
                onClick={onNavigateNikolayFitting}
                endIcon={
                  <ArrowSplideRight
                    sx={{
                      width: { xs: 12, md: 15 },
                      height: { xs: 12, md: 15 },
                    }}
                    htmlColor={theme.palette.customColor?.light}
                  />
                }
                sx={{
                  backgroundColor: theme.palette.secondary.dark,
                  padding: "12px 15px !important",
                  fontSize: { xs: 12, md: 15 },
                  color: theme.palette.customColor?.main,
                  borderRadius: "100vmax !important",
                }}
              >
                Fitting with Nikolay
              </Button>
            </Box>
          </Box>
        </Dialog>
      </Box>
    </Fade>
  );
};
